import React, { useId, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDealershipSettings } from '../../contexts/settingsContext';
import { useNavigate } from 'react-router-dom';
import { gapSeverity, formatMoneyShort, LOT_VALUE_TIPS } from '../../utils/lotValue';
import { buildAiValueView, compareNullsLast, gapScaleOf } from '../../utils/branchView';
import './BranchAiValueTable.css';

// "+6.7%" / "−10.0%" / "0.0%"; missing → "—".
export const formatGapPct = (gapPct) => {
    if (gapPct === null || gapPct === undefined || Number.isNaN(gapPct)) return '—';
    const pct = gapPct * 100;
    const body = Math.abs(pct).toFixed(1);
    if (body === '0.0') return '0.0%';
    return `${pct < 0 ? '−' : '+'}${body}%`;
};

// "+$862k" / "−$10k" / "$0" — same explicit sign as the lot value tile.
export const formatGapMoney = (value) => {
    const text = formatMoneyShort(value);
    return typeof value === 'number' && value > 0 && text !== '$0' ? `+${text}` : text;
};

const SORT_KEYS = { gapTotal: 'Gap $', gapPct: 'Gap %' };

// Shared with the v2 table (BranchesV2/AiValueTableV2).
export const GAP_TIP = `${LOT_VALUE_TIPS.gap}.`;
export const GAP_PCT_TIP = 'Gap as a share of the AI value. Bars share one scale, set by the largest gap in the table.';
export const SELECTION_LABEL = 'Selection';
export const usedUnitsTip = (priced, used) => `Used units with both an asking price and an AI price (${
    priced.toLocaleString('en-US')} of ${used.toLocaleString('en-US')})`;
export const AI_VALUE_EMPTY = Object.freeze({
    loading: 'Loading inventory…',
    dealership: 'No used units have both an asking price and an AI price yet.',
    selection: 'No used units in this selection have both an asking price and an AI price.',
});

// Rows without a value (gapPct null) always sink to the bottom.
export const compareRows = (key, dir) => (a, b) => compareNullsLast(dir)(a[key], b[key]);

// Colour carries the same meaning as the lot value tile: amber / red above the
// warn / critical thresholds, everything else neutral.
export const severityClass = (gapPct, thresholds) => {
    const severity = gapSeverity(gapPct, thresholds);
    return severity === 'warn' || severity === 'critical' ? severity : 'neutral';
};

// Header tooltip: a focusable (i) trigger with an aria-describedby
// role="tooltip" — the lot value tile pattern, so it opens on hover, keyboard
// focus and tap. (The shared InfoTip is mouse-only.) Kept outside the sort
// button so there are no nested interactive controls.
function HeaderTip({ id, text }) {
    return (
        <span className="bav-tip-trigger" tabIndex={0} aria-describedby={id}>
            <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12" aria-hidden="true" focusable="false">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span id={id} role="tooltip" className="bav-tip">{text}</span>
        </span>
    );
}

HeaderTip.propTypes = {
    id: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
};

// Inline diverging bar: zero line in the middle, positive gaps (listed above
// the AI value) grow right, negative ones left. `scale` is the largest |gap%|
// on screen, so every bar shares one symmetric scale; a full half-track (50%)
// equals that largest gap. `muted` = the Dealership reference row: no
// severity colour, bar in the muted reference style.
function GapPctCell({ gapPct, scale, muted }) {
    const { gapThresholds } = useDealershipSettings();
    const sev = muted ? 'ref' : severityClass(gapPct, gapThresholds);
    const hasValue = typeof gapPct === 'number' && !Number.isNaN(gapPct);
    const width = hasValue && scale > 0 ? Math.min(Math.abs(gapPct) / scale, 1) * 50 : 0;
    const dir = hasValue && gapPct < 0 ? 'neg' : 'pos';
    return (
        <td className={`bav-r bav-gap--${sev}`}>
            <span className="bav-gap-pct">
                <span className="bav-diverge" aria-hidden="true">
                    <span className="bav-zero" />
                    <span
                        className={`bav-bar bav-bar--${dir} bav-bar--${sev}`}
                        style={{ width: `${width}%` }}
                    />
                </span>
                <span className="bav-gap-pct-value">{formatGapPct(gapPct)}</span>
            </span>
        </td>
    );
}

GapPctCell.propTypes = {
    gapPct: PropTypes.number,
    scale: PropTypes.number.isRequired,
    muted: PropTypes.bool,
};
GapPctCell.defaultProps = { gapPct: null, muted: false };

// Non-clickable summary row: the bold "Selection" / "Dealership" total, or the
// muted "Dealership" reference row shown under a partial selection.
function SummaryRow({ summary, label, variant, scale }) {
    const { gapThresholds } = useDealershipSettings();
    const muted = variant === 'ref';
    return (
        <tr className={muted ? 'bav-ref-row' : 'bav-total-row'}>
            <td className="bav-branch">{label}</td>
            <td className="bav-r">{summary.pricedCount.toLocaleString('en-US')}</td>
            <td className="bav-r">{formatMoneyShort(summary.askingTotal)}</td>
            <td className="bav-r">{formatMoneyShort(summary.aiTotal)}</td>
            <td className={muted ? 'bav-r' : `bav-r bav-gap--${severityClass(summary.gapPct, gapThresholds)}`}>
                {formatGapMoney(summary.gapTotal)}
            </td>
            <GapPctCell gapPct={summary.gapPct} scale={scale} muted={muted} />
        </tr>
    );
}

SummaryRow.propTypes = {
    summary: PropTypes.shape({
        pricedCount: PropTypes.number,
        askingTotal: PropTypes.number,
        aiTotal: PropTypes.number,
        gapTotal: PropTypes.number,
        gapPct: PropTypes.number,
    }).isRequired,
    label: PropTypes.string.isRequired,
    variant: PropTypes.oneOf(['total', 'ref']).isRequired,
    scale: PropTypes.number.isRequired,
};

/**
 * Asking vs AI value per branch, linked to the Branches Overview selector.
 *
 * - `selectedBranches` null, or `allSelected`: every branch + a bold
 *   "Dealership" total.
 * - Partial selection: only the selected branches, a bold "Selection" total
 *   when more than one is selected, and a muted "Dealership" reference row
 *   (all branches) for comparison.
 */
export default function BranchAiValueTable({
    units, loading, selectedBranches, allSelected,
}) {
    const { gapThresholds } = useDealershipSettings();
    const navigate = useNavigate();
    const baseId = `bav-${useId().replace(/:/g, '')}`;
    const [sort, setSort] = useState({ key: 'gapTotal', dir: 'desc' });

    const showAll = allSelected || !selectedBranches;

    // Rows, totals and coverage (utils/branchView, shared with the v2 table).
    const view = useMemo(
        () => buildAiValueView(units, selectedBranches, allSelected),
        [units, selectedBranches, allSelected],
    );

    const { rows, selection, dealership, usedUnits } = view;

    // One symmetric scale for every bar on screen — summary rows included, so
    // a Dealership reference larger than the selected rows is never clipped.
    const gapScale = useMemo(() => gapScaleOf([...rows, selection, dealership]), [rows, selection, dealership]);

    const sortedRows = useMemo(
        () => [...rows].sort(compareRows(sort.key, sort.dir)),
        [rows, sort],
    );

    const onSort = (key) => {
        setSort((prev) => (prev.key === key
            ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
            : { key, dir: 'desc' }));
    };

    // The SPA keeps the window scroll across routes, and this table sits far
    // down the Branches page — without the reset the inventory page opens
    // part-way down with the lot value tile and the branch filter off-screen.
    // Reset here (not on the inventory side) so only this entry point is
    // affected; other ways back to the inventory keep their behaviour.
    const openBranch = (branch) => {
        navigate(`/?branch=${encodeURIComponent(branch)}`);
        // 'instant' overrides the global `html { scroll-behavior: smooth }`.
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    };

    const sortHeader = (key, tip) => {
        const active = sort.key === key;
        const ariaSort = active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined;
        return (
            <th scope="col" className="bav-r" aria-sort={ariaSort}>
                <span className="bav-th">
                    <button type="button" className="bav-sort-btn" onClick={() => onSort(key)}>
                        {SORT_KEYS[key]}
                        {active
                            ? <span className="bav-sort-icon active" aria-hidden="true">{sort.dir === 'asc' ? '▲' : '▼'}</span>
                            : <span className="bav-sort-icon muted" aria-hidden="true">&#8597;</span>}
                    </button>
                    <HeaderTip id={`${baseId}-tip-${key}`} text={tip} />
                </span>
            </th>
        );
    };

    const tipHeader = (label, key, tip) => (
        <th scope="col" className="bav-r">
            <span className="bav-th">
                {label}
                <HeaderTip id={`${baseId}-tip-${key}`} text={tip} />
            </span>
        </th>
    );

    // Coverage of what the rows show: the selection, or the whole dealership.
    const usedTip = usedUnitsTip(view.scopePriced, view.scopeUsed);

    let body;
    if (loading && usedUnits.length === 0) {
        body = <div className="bav-empty">{AI_VALUE_EMPTY.loading}</div>;
    } else if (dealership.pricedCount === 0) {
        body = (
            <div className="bav-empty">
                {AI_VALUE_EMPTY.dealership}
            </div>
        );
    } else if (rows.length === 0) {
        body = (
            <div className="bav-empty">
                {AI_VALUE_EMPTY.selection}
            </div>
        );
    } else {
        body = (
            <div className="bav-scroll">
                <table className="bav-table">
                    <thead>
                        <tr>
                            <th scope="col">Branch</th>
                            {tipHeader('Used units', 'used', usedTip)}
                            {tipHeader('Asking', 'asking', LOT_VALUE_TIPS.asking)}
                            {tipHeader('AI value', 'ai', LOT_VALUE_TIPS.ai)}
                            {sortHeader('gapTotal', GAP_TIP)}
                            {sortHeader('gapPct', GAP_PCT_TIP)}
                        </tr>
                    </thead>
                    <tbody>
                        {sortedRows.map((r) => (
                            <tr
                                key={r.branch}
                                className="bav-row"
                                title={`View ${r.branch} inventory`}
                                onClick={() => openBranch(r.branch)}
                            >
                                <td className="bav-branch">
                                    {/* The whole row is a mouse target; the button is the
                                        keyboard / screen-reader control (Enter and Space). */}
                                    <button
                                        type="button"
                                        className="bav-branch-btn"
                                        aria-label={`Open ${r.branch} inventory`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openBranch(r.branch);
                                        }}
                                    >
                                        {r.branch}
                                    </button>
                                </td>
                                <td className="bav-r">{r.pricedCount.toLocaleString('en-US')}</td>
                                <td className="bav-r">{formatMoneyShort(r.askingTotal)}</td>
                                <td className="bav-r">{formatMoneyShort(r.aiTotal)}</td>
                                <td className={`bav-r bav-gap--${severityClass(r.gapPct, gapThresholds)}`}>
                                    {formatGapMoney(r.gapTotal)}
                                </td>
                                <GapPctCell gapPct={r.gapPct} scale={gapScale} />
                            </tr>
                        ))}
                        {selection && (
                            <SummaryRow
                                summary={selection}
                                label={SELECTION_LABEL}
                                variant="total"
                                scale={gapScale}
                            />
                        )}
                        <SummaryRow
                            summary={dealership}
                            label={dealership.branch}
                            variant={showAll ? 'total' : 'ref'}
                            scale={gapScale}
                        />
                    </tbody>
                </table>
            </div>
        );
    }

    return (
        <section className="bav-section" aria-labelledby={`${baseId}-title`}>
            {/* Same section label as the neighbouring detail-panel sections
                ("Anomaly Signals", "Price Alignment vs AI"). */}
            <h3 id={`${baseId}-title`} className="bco-section-title bco-section-title-mt bav-title">
                AI value by location
            </h3>
            {body}
        </section>
    );
}

BranchAiValueTable.propTypes = {
    units: PropTypes.arrayOf(PropTypes.object),
    loading: PropTypes.bool,
    // Branch names picked in the Branches Overview selector; null = all.
    selectedBranches: PropTypes.oneOfType([
        PropTypes.instanceOf(Set),
        PropTypes.arrayOf(PropTypes.string),
    ]),
    allSelected: PropTypes.bool,
};

BranchAiValueTable.defaultProps = {
    units: [],
    loading: false,
    selectedBranches: null,
    allSelected: false,
};
